import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate } from "react-router-dom";
import Icon from "../components/Icon";
import { useAuth } from "../contexts/AuthContext";
import { homePathForRole } from "../utils/roleHome";
import { submitLead } from "../api/leadService";
import "./landing.css";

const COMPLIANCE = ["GST", "TDS", "ITR", "ROC / MCA", "Advance Tax", "Audit"];

/* The recorded product tour. The -v1 suffix is load-bearing: CRA does not
   content-hash anything in public/, so a re-encode published under the same
   name would sit behind browser and nginx caches. Bump to -v2 on any re-encode
   — which is also what makes an immutable, year-long cache header safe. */
const TOUR = {
  src: "/media/smartca-tour-v1.mp4",
  poster: "/media/smartca-tour-poster-v1.jpg",
  // Matches what the native controls read out (3:57), not the rounded 237.6s.
  length: "3 min 57 sec",
};

/* The module index — presented as a ledger's table of contents. */
const INDEX = [
  { title: "Task oversight", body: "Assign, review and verify every job across the firm before it's signed off." },
  { title: "Auto compliance", body: "GSTR, TDS, ITR and ROC tasks create themselves each period, for every applicable client." },
  { title: "GST invoicing", body: "Branded, GST-compliant invoices out the door as clean PDFs." },
  { title: "Payments & dues", body: "Who owes what and who paid when, with reusable fee templates across your book." },
  { title: "Attendance & leave", tag: "New", body: "Check-ins, shifts, late marks and leave balances — tallied for you, not by you." },
  { title: "Client portal", body: "A tidy home for each client's documents and support tickets. No more email threads." },
  { title: "Credential vault", body: "Client GST, IT and TRACES logins encrypted at rest. Never a shared spreadsheet again." },
  { title: "Team chat", body: "1:1 and firm-wide messages with files, without leaving the app." },
  { title: "Compliance calendar", body: "Every deadline on one month view — per person, or the whole firm at once." },
  { title: "Document drive", body: "A shared, organised drive for working papers and everything clients send you." },
];

const CONSOLES = [
  { who: "Partners & owners", title: "Admin console", body: "Full control of clients, tasks, staff, billing and settings — with a live view of the whole firm." },
  { who: "Your staff", title: "Employee console", body: "A focused board of their own work; partners grant access to clients, billing and the KB as needed." },
  { who: "Your clients", title: "Client portal", body: "A clean home for the documents and support the firm chooses to share. Nothing they shouldn't see." },
];

const STEPS = [
  { title: "Tell us about the firm", body: "We create your firm, seats and the first admin login. You're in the same day — no IT project." },
  { title: "Load your templates", body: "Describe recurring work once — say, Monthly GST. Tasks then create themselves every period, per client." },
  { title: "Let the season run itself", body: "Staff work their boards, partners watch one dashboard, clients self-serve in the portal." },
];

const PROMISES = [
  { title: "Nothing falls through the cracks", body: "Recurring compliance is generated, assigned and reviewed before it's marked done." },
  { title: "Partners stay in control", body: "One live dashboard — who's behind, what's overdue, what's waiting on review." },
  { title: "Clients feel looked after", body: "Their own portal for documents and requests, instead of scattered chats." },
];

const INCLUDED = [
  "Admin, employee & client portals",
  "Auto recurring compliance tasks",
  "Reviewer & verification workflow",
  "GST invoicing + payment tracking",
  "Encrypted client credential vault",
  "Client support desk (tickets)",
];

const ADDONS = [
  { icon: "clock", name: "Attendance & Leave", tag: "New", body: "Shifts, check-in / out, leave balances and a colour-coded monthly muster with CSV reports." },
  { icon: "map-pin", name: "Geofencing", body: "Restrict staff sign-in to areas you draw on a map." },
  { icon: "message-circle", name: "Team Chat", body: "Internal staff messaging with history & files." },
  { icon: "bell", name: "Realtime Notifications", body: "Push notifications & live auto-refresh on every device." },
  { icon: "hard-drive", name: "Extra Storage", body: "Grow the shared drive as your document vault fills up." },
];

const FAQ = [
  { q: "How do people sign in?", a: "You issue every login and hand over a one-time password; the app walks each user through setting their own on first sign-in. No signup forms, no email hoops." },
  { q: "Can it handle attendance and leave?", a: "Yes — with the Attendance & Leave add-on, staff check in and out from the app itself. Shifts and week patterns (including alternate Saturdays) are supported, late marks and half-days are computed automatically, and leave requests carry balances and approvals. Admins get a colour-coded monthly muster plus CSV reports, and every correction is kept in an audit trail." },
  { q: "Is our data secure and separate?", a: "Every firm's data is fully isolated from every other firm. Sensitive credentials are encrypted at rest, and repeated failed logins lock the account automatically." },
  { q: "Does it work on mobile?", a: "Yes — Smart CA installs like a native app on Android, iPhone and desktop (PWA), with its own icon, full-screen layout and push notifications." },
  { q: "How is it priced?", a: "₹100 per seat per month, billed yearly. You pay for the staff and admin logins your firm needs, plus optional add-ons (attendance & leave, team chat, realtime notifications, geofencing, extra storage). Request a demo and we'll work out the numbers for your firm." },
];

const ADDRESS = "430, 4th floor, Bricks & Palms, 36-14-3/2, Mogalrajapuram Rd, Mogalrajapuram, Shanti Nagar, Labbipet, Vijayawada, Andhra Pradesh - 522237";

export default function LandingPage() {
  const { isAuthenticated, isLoading, role } = useAuth();

  // Scroll-reveal: flip `.in` on [data-reveal] elements as they enter the
  // viewport. Depends on auth state so it re-runs once the marketing DOM
  // actually renders (the early returns below mean it may find nothing first).
  useEffect(() => {
    if (isLoading || isAuthenticated) return undefined;
    const els = Array.from(document.querySelectorAll(".lp [data-reveal]"));
    if (!els.length) return undefined;
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -36px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [isLoading, isAuthenticated]);

  // Wait for auth hydration so a logged-in user doesn't flash the landing page.
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to={homePathForRole(role)} replace />;

  // Smooth-scroll every in-page anchor (Get a demo, nav, footer) via delegation.
  const onAnchorClick = (e) => {
    const anchor = e.target.closest?.('a[href^="#"]');
    if (!anchor) return;
    const el = document.getElementById(anchor.getAttribute("href").slice(1));
    if (el) {
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="lp" onClick={onAnchorClick}>
      <SiteHeader />

      {/* ── Hero — ruled ledger paper ── */}
      <section className="lp-hero">
        <div className="lp-hero-margin" aria-hidden />
        <div className="lp-container lp-hero-inner">
          <div className="lp-hero-copy">
            <div className="lp-eyebrow">Practice management · made in India, for Indian CAs</div>
            <h1 className="lp-hero-title">
              Every client, every deadline,{" "}
              <em className="lp-hand">
                one well-kept register.
                <svg viewBox="0 0 200 10" preserveAspectRatio="none" aria-hidden>
                  <path pathLength="1" d="M3 6.5 C 42 2, 84 9, 122 5 S 182 6.5, 197 4" />
                </svg>
              </em>
            </h1>
            <p className="lp-hero-tagline">
              Smart CA is the system a busy firm actually runs on — recurring compliance that files itself
              into your task board, GST invoicing, staff attendance, documents, and a portal your clients
              genuinely use. No spreadsheets held together by memory. No midnight WhatsApp scrolls.
            </p>
            <div className="lp-hero-cta">
              <a href="#contact" className="lp-btn lp-btn--primary lp-btn--lg">Get a demo <Icon name="arrow-right" size={15} /></a>
              <Link to="/login" className="lp-btn lp-btn--ghost lp-btn--lg">Sign in</Link>
            </div>
            <ul className="lp-hero-meta">
              <li><Icon name="check" size={13} /> You issue every login — no public signups</li>
              <li><Icon name="check" size={13} /> Installs on any phone, no app store</li>
              <li><Icon name="check" size={13} /> Your data stays your firm's</li>
            </ul>
          </div>
          <div className="lp-hero-visual">
            <TourPlayer />
            <InkStamp />
          </div>
        </div>
      </section>

      {/* ── Scope line ── */}
      <div className="lp-ledgerline">
        <div className="lp-container lp-ledgerline-inner">
          <span className="lp-ll-label">In scope</span>
          {COMPLIANCE.map((c) => <span key={c} className="lp-ll-item">{c}</span>)}
          <span className="lp-ll-etc">…and whatever else the season brings</span>
        </div>
      </div>

      {/* ── Why — scroll-driven story ── */}
      <ScrollStory id="why" steps={STORY_STEPS}>
        <StoryScene />
      </ScrollStory>

      {/* ── 01 · The index ── */}
      <Section id="features">
        <SectionHead no="01" title="Everything in its place" sub="Ten modules that replace the tangle of spreadsheets, group chats and pocket diaries a practice quietly runs on." />
        <ol className="lp-index">
          {INDEX.map((f, i) => (
            <li className="lp-index-item" key={f.title} data-reveal style={{ "--d": `${i * 40}ms` }}>
              <span className="lp-index-no">{String(i + 1).padStart(2, "0")}</span>
              <div className="lp-index-text">
                <div className="lp-index-title">
                  {f.title}
                  {f.tag && <span className="lp-tag">{f.tag}</span>}
                </div>
                <div className="lp-index-body">{f.body}</div>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      {/* ── 02 · Attendance — scroll story ── */}
      <ScrollStory id="attendance" className="lp-story--mini lp-story--tint" steps={ATT_STEPS}>
        <AttendanceScene />
      </ScrollStory>

      {/* ── 03 · Oversight spotlight ── */}
      <Section>
        <div className="lp-split lp-rev">
          <div className="lp-split-visual"><OversightMock /></div>
          <div className="lp-split-copy" data-reveal>
            <div className="lp-kicker"><span className="lp-kicker-no">№ 03</span> Command centre</div>
            <h2>The whole firm, on one screen.</h2>
            <p>
              Filter every task by staff, client, status or due date. Bulk-assign work, set reviewers,
              and verify completed work before it's signed off — so nothing slips, and partners always
              know exactly where things stand.
            </p>
            <ul className="lp-check-list">
              <li><Icon name="check" size={14} /> Live overdue / due-today / this-week view</li>
              <li><Icon name="check" size={14} /> Reviewer &amp; verification workflow</li>
              <li><Icon name="check" size={14} /> Recurring compliance generated automatically</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ── 04 · Geofence — scroll story ── */}
      <ScrollStory id="geofence" className="lp-story--mini lp-story--tint lp-story--rev" steps={GEO_STEPS}>
        <GeoScene />
      </ScrollStory>

      {/* ── 05 · Consoles as file folders ── */}
      <Section id="consoles">
        <SectionHead no="05" title="Three doors into the same office" sub="Everyone signs in at the same place — the app opens the right console for who they are." />
        <div className="lp-folders">
          {CONSOLES.map((c, i) => (
            <div className="lp-folder" key={c.title} data-reveal style={{ "--d": `${i * 90}ms` }}>
              <div className="lp-folder-tab">{c.who}</div>
              <div className="lp-folder-body">
                <div className="lp-folder-title">{c.title}</div>
                <p>{c.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 06 · How it works ── */}
      <Section tint>
        <SectionHead no="06" title="Live by Monday" sub="Getting started is a conversation, not a project." />
        <div className="lp-steps" data-reveal="bare">
          {STEPS.map((s, i) => (
            <div className="lp-step" key={s.title} data-reveal style={{ "--d": `${i * 120}ms` }}>
              <div className="lp-step-num">{i + 1}</div>
              <div className="lp-step-title">{s.title}</div>
              <div className="lp-step-body">{s.body}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Origin band ── */}
      <section className="lp-band">
        <div className="lp-container lp-band-inner">
          <div className="lp-band-quote" data-reveal>
            <p>
              “Smart CA wasn't invented in a boardroom. It began as the in-house system of one busy
              practice — shaped filing season after filing season, until the whole office ran on it.
              Now it can run yours.”
            </p>
            <div className="lp-band-sig">— Team Devsoliel · Vijayawada</div>
          </div>
          <div className="lp-band-side" data-reveal="bare">
            <div className="lp-band-h">What that means for you</div>
            <ul>
              {PROMISES.map((p) => (
                <li key={p.title}>
                  <Icon name="check-circle" size={16} />
                  <div><b>{p.title}</b><span>{p.body}</span></div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── 07 · Pricing ── */}
      <Section id="pricing">
        <SectionHead no="07" center title="Plain pricing, no surprises" sub="An annual per-seat subscription for the core practice, with optional add-ons on top." />
        <div className="lp-pricing">
          <div className="lp-price-card" data-reveal>
            <div className="lp-price-kicker">Core plan</div>
            <div className="lp-price-line"><span className="lp-price-amt">₹100</span><span className="lp-price-per">per seat / month</span></div>
            <p className="lp-price-note">Billed yearly. You pay for the number of staff &amp; admin logins your firm needs. Trials available.</p>
            <ul className="lp-check-list">
              {INCLUDED.map((x) => <li key={x}><Icon name="check" size={14} /> {x}</li>)}
            </ul>
            <a href="#contact" className="lp-btn lp-btn--primary lp-btn--lg lp-price-cta">Get a quote <Icon name="arrow-right" size={15} /></a>
          </div>
          <div className="lp-addons">
            <div className="lp-addons-title">Optional add-ons</div>
            {ADDONS.map((a, i) => (
              <div className="lp-addon" key={a.name} data-reveal style={{ "--d": `${i * 70}ms` }}>
                <Icon name={a.icon} size={16} />
                <div>
                  <b>{a.name} {a.tag && <span className="lp-tag">{a.tag}</span>}</b>
                  <span className="lp-addon-body">{a.body}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── 08 · FAQ ── */}
      <Section id="faq" tint>
        <SectionHead no="08" center title="Fair questions" />
        <div className="lp-faq">
          {FAQ.map((f, i) => (
            <div key={f.q} data-reveal style={{ "--d": `${i * 60}ms` }}>
              <FaqItem q={f.q} a={f.a} />
            </div>
          ))}
        </div>
      </Section>

      {/* ── Contact ── */}
      <section className="lp-section" id="contact">
        <div className="lp-container lp-contact">
          <div className="lp-contact-info" data-reveal>
            <h2>See it with your own eyes</h2>
            <p>Tell us a little about your practice and we'll walk you through Smart CA on a call — usually within a day.</p>
            <div className="lp-address">
              <Icon name="building" size={16} />
              <span>{ADDRESS}</span>
            </div>
          </div>
          <LeadForm />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ── Layout helpers ── */
function SiteHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="lp-header">
      <div className="lp-container lp-header-inner">
        <div className="lp-brand">
          <img src="/logo.svg" alt="Devsoleil" className="lp-brand-logo" />
          <span className="lp-brand-name">Smart CA</span>
        </div>
        <nav className={`lp-nav ${open ? "open" : ""}`}>
          <a href="#features" className="lp-nav-link" onClick={() => setOpen(false)}>Features</a>
          <a href="#attendance" className="lp-nav-link lp-nav-link--new" onClick={() => setOpen(false)}>Attendance</a>
          <a href="#pricing" className="lp-nav-link" onClick={() => setOpen(false)}>Pricing</a>
          <a href="#faq" className="lp-nav-link" onClick={() => setOpen(false)}>FAQ</a>
          <a href="#contact" className="lp-btn lp-btn--primary" onClick={() => setOpen(false)}>Get a demo</a>
          <Link to="/login" className="lp-btn lp-btn--ghost">Sign in</Link>
        </nav>
        <button className="lp-burger" aria-label="Menu" onClick={() => setOpen((v) => !v)}>
          <Icon name={open ? "x" : "list"} size={20} />
        </button>
      </div>
    </header>
  );
}

function Section({ id, tint, children }) {
  return (
    <section className={`lp-section ${tint ? "lp-section--tint" : ""}`} id={id}>
      <div className="lp-container">{children}</div>
    </section>
  );
}

function SectionHead({ no, title, sub, center }) {
  return (
    <div className={`lp-head ${center ? "lp-head--center" : ""}`} data-reveal>
      <div className="lp-head-rule" aria-hidden />
      <div className="lp-head-row">
        <span className="lp-head-no">{no}</span>
        <h2>{title}</h2>
      </div>
      {sub && <p>{sub}</p>}
    </div>
  );
}

/* Scroll-reveal lives on the wrapper in the FAQ list, NOT on this element:
   React rewrites className when `open` toggles, which would wipe the
   observer-added `.in` class and re-hide the card. The answer stays mounted
   and collapses via grid-template-rows so both directions animate. */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`lp-faq-item ${open ? "open" : ""}`}>
      <button className="lp-faq-q" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span>{q}</span>
        <Icon name="chevron-down" size={16} />
      </button>
      <div className="lp-faq-body" aria-hidden={!open}>
        <div className="lp-faq-a"><p>{a}</p></div>
      </div>
    </div>
  );
}

/* The recorded product tour — the hero's visual, in place of the CSS admin mock
   this replaced.

   preload="none" is the load-bearing attribute: the 24 MB MP4 is never fetched
   until the visitor actually presses play, so the hero costs only the ~35 KB
   poster. It is also why the poster is a separate <img> rather than the video's
   own poster= attribute — that keeps the two fetches independently controllable.

   The hero itself never plays: pressing play opens TourModal below. A
   470px-wide slot is no place to watch four narrated minutes, and keeping the
   <video> out of the hero entirely means the MP4 cannot be fetched until the
   dialog mounts — the strongest possible form of "nothing until asked". */
function TourPlayer() {
  const [open, setOpen] = useState(false);
  const playRef = useRef(null);

  // Returning focus to the button the dialog was opened from is the half of
  // modal focus management that is easiest to forget and most obvious to a
  // keyboard user when it's missing.
  const close = useCallback(() => {
    setOpen(false);
    playRef.current?.focus();
  }, []);

  return (
    <>
      <div className="lp-tour-frame">
        {/* The browser chrome the old CSS mock wore, kept so the hero visual still
            reads as a window onto the app rather than as an embedded video */}
        <div className="lp-shot-bar">
          <div className="lp-shot-dots"><i /><i /><i /></div>
          <div className="lp-shot-title">smartca.devsoleil.com — recorded product tour</div>
        </div>

        <div className="lp-tour-screen">
          {/* Eager and high priority, deliberately: in the hero this poster is
              above the fold and becomes the LCP element, so deferring it would
              only delay the number it now defines. Kept to 1280x720 (~35 KB)
              for the same reason — it renders at ~480px wide. */}
          <img
            className="lp-tour-poster"
            src={TOUR.poster}
            alt=""
            width="1280"
            height="720"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <button
            type="button"
            className="lp-tour-play"
            ref={playRef}
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-label={`Play the Smart CA product tour — ${TOUR.length}, narrated. Opens in a dialog.`}
          >
            <span className="lp-tour-disc"><Icon name="play" size={22} fill="currentColor" /></span>
            <span className="lp-tour-label">Play the tour</span>
            <span className="lp-tour-time">{TOUR.length} · narrated</span>
          </button>
        </div>
      </div>

      {/* Portalled to <body> deliberately. .lp-hero-visual carries a transform
          (its entrance animation settles on an identity matrix rather than
          `none`), and ANY transform makes an element the containing block for
          position:fixed descendants — rendered in place, the dialog was being
          trapped inside the ~480px hero column instead of covering the
          viewport. The portal also keeps it clear of the page's stacking
          contexts. */}
      {open && createPortal(<TourModal onClose={close} />, document.body)}
    </>
  );
}

/* The tour, played full-attention over a dimmed page.
   Mounted only while open, so the 24 MB MP4 is requested at this moment and
   never before — and unmounting on close stops playback outright, with no
   stray audio left running behind a closed dialog. */
function TourModal({ onClose }) {
  const videoRef = useRef(null);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevPad = body.style.paddingRight;
    // Lock the page behind the dialog, padding by exactly the scrollbar width
    // so the content underneath doesn't shift as it disappears.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (gap > 0) body.style.paddingRight = `${gap}px`;

    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab") return;
      // Minimal focus trap: only the close button and the video are reachable,
      // so wrap between them rather than pulling in a focus-trap dependency.
      const focusable = dialogRef.current?.querySelectorAll("button, video, a[href]");
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();

    // The opening click is still a valid activation, so this is normally
    // allowed; if a browser refuses, the native controls are right there.
    const p = videoRef.current?.play();
    if (p && p.catch) p.catch(() => {});

    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPad;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    /* The `lp` class is not decoration: every --lp-* token is defined on .lp,
       and portalling to <body> puts this dialog outside that scope, so without
       it the paper mount and its rules resolve to nothing and the page shows
       straight through. .lp's own background is overridden by .lp-tour-modal's,
       which is declared later in the stylesheet. */
    <div
      className="lp lp-tour-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Smart CA product tour"
      onClick={onClose}
    >
      {/* Clicking the backdrop closes; clicking the player must not. */}
      <div className="lp-tour-dialog" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
        <div className="lp-tour-dialog-bar">
          <span className="lp-tour-dialog-title">Smart CA — recorded product tour</span>
          <button type="button" className="lp-tour-close" ref={closeRef} onClick={onClose} aria-label="Close the tour">
            <Icon name="x" size={16} />
          </button>
        </div>

        {failed ? (
          <div className="lp-tour-failed">
            <b>The tour didn’t load.</b>
            <a href="#contact" className="lp-btn lp-btn--primary" onClick={onClose}>
              Get a live walkthrough instead
            </a>
          </div>
        ) : (
          /* src on the element, NOT a <source> child: with <source> the error
             event fires on the source and never bubbles, so onError would never
             run and a broken file would spin forever. */
          <video
            ref={videoRef}
            className="lp-tour-modal-video"
            src={TOUR.src}
            width="1920"
            height="1080"
            preload="auto"
            autoPlay
            playsInline
            controls
            controlsList="nodownload"
            onError={() => setFailed(true)}
          >
            Your browser can’t play this video. <a href={TOUR.src}>Download the tour (MP4)</a>.
          </video>
        )}
      </div>
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="lp-footer">
      <div className="lp-container lp-footer-grid">
        <div className="lp-footer-brand">
          <div className="lp-brand">
            <img src="/logo.svg" alt="Devsoleil" className="lp-brand-logo" />
            <span className="lp-brand-name">Smart CA</span>
          </div>
          <p>Practice management for Indian Chartered Accountants. A Devsoleil product.</p>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-h">Product</div>
          <a href="#features">Features</a>
          <a href="#attendance">Attendance</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-h">Company</div>
          <a href="#contact">Contact</a>
          <Link to="/login">Sign in</Link>
        </div>
        <div className="lp-footer-col">
          <div className="lp-footer-h">Get in touch</div>
          <span className="lp-footer-muted">{ADDRESS}</span>
        </div>
      </div>
      <div className="lp-container lp-footer-legal">
        Smart CA is practice-management software by Devsoleil and is not affiliated with, endorsed by,
        or approved by the Institute of Chartered Accountants of India (ICAI).
      </div>
      <div className="lp-container lp-footer-bottom">
        © {new Date().getFullYear()} Devsoleil · Smart CA · Made with care in Vijayawada, India
      </div>
    </footer>
  );
}

/* ── Scroll stories ──
   A sticky scene pinned while three copy steps scroll past; the active step
   flips the frame's stage class (s0/s1/s2) and CSS transitions do the acting.
   Reused for the "why", attendance and geofence chapters. */
const STORY_STEPS = [
  {
    k: "The old way",
    t: "Right now, your practice runs on memory.",
    b: "Deadlines live in WhatsApp chats, one overworked Excel sheet, and the senior who remembers everything. It works — until the one week everyone is busy.",
  },
  {
    k: "What it costs",
    t: "One missed date is never just a date.",
    b: "It's a late fee and interest. It's an awkward phone call. It's a client who heard about the notice before they heard from you.",
  },
  {
    k: "The Smart CA way",
    t: "So the register keeps itself.",
    b: "Every recurring compliance creates its own task, lands on the right person's board, and can't be signed off until it's reviewed. You stop remembering. The system does.",
    link: { href: "#features", label: "See everything it replaces" },
  },
];

const ATT_STEPS = [
  {
    no: "№ 02",
    k: "New add-on — Attendance & Leave",
    t: "Check-in lives where your team already works.",
    b: "One tap in the app's top bar — no biometric machine, no separate attendance app. With the geofence add-on, every punch is location-tagged too.",
  },
  {
    k: "The engine",
    t: "Late marks do their own math.",
    b: "Checked in at 9:42 against a 9:30 shift? Late mark. Left 9 minutes short? Shortfall. Approved half-day? Counted. Shifts, week patterns and alternate Saturdays included.",
  },
  {
    k: "The register",
    t: "And the muster writes itself.",
    b: "A colour-coded month for every person — presents, halves, leaves, holidays — exportable as CSV across ten reports, with every admin correction kept in an audit trail.",
  },
];

const GEO_STEPS = [
  {
    no: "№ 04",
    k: "Add-on — Geofencing",
    t: "Draw your office on a real map.",
    b: "An area, not an address. Office, branch or client site — draw it once and assign the people who work from there.",
  },
  {
    k: "Inside",
    t: "Inside the fence, work opens.",
    b: "Assigned staff can sign in only while they're physically inside — and their attendance punches carry the geofence tag automatically.",
  },
  {
    k: "Outside",
    t: "Step out, and it locks.",
    b: "Leave the area and the app locks; step back in and it reopens. Location is only compared against the area — no tracking, no location history.",
  },
];

function ScrollStory({ id, className = "", steps, children }) {
  const [stage, setStage] = useState(0);
  const stepsRef = useRef(null);

  useEffect(() => {
    const els = Array.from(stepsRef.current?.querySelectorAll(".lp-story-step") || []);
    if (!els.length || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setStage(Number(e.target.dataset.step));
        });
      },
      { threshold: 0.5 }
    );
    els.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  return (
    <section className={`lp-story ${className}`} id={id}>
      <div className="lp-story-pin">
        <div className={`lp-container lp-story-frame s${stage}`}>
          <div className="lp-story-copyspace" aria-hidden />
          {children}
        </div>
      </div>
      <div className="lp-story-steps" ref={stepsRef}>
        {steps.map((s, i) => (
          <div className="lp-story-step" key={s.t} data-step={i}>
            <div className="lp-container lp-story-step-inner">
              <div className={`lp-story-card ${stage === i ? "on" : ""}`}>
                <div className="lp-kicker">
                  {s.no && <span className="lp-kicker-no">{s.no}</span>}
                  {s.k}
                </div>
                <h3>{s.t}</h3>
                <p>{s.b}</p>
                {s.link && (
                  <a href={s.link.href} className="lp-story-link">
                    {s.link.label} <Icon name="arrow-down" size={14} />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StoryScene() {
  return (
    <div className="lp-scene" aria-hidden>
      {/* the chaos */}
      <div className="lp-scrap lp-scrap--chat">
        <b>Mehta Traders</b>
        <span>Sir, is our GST filed?? Please confirm</span>
        <i>11:48 pm</i>
      </div>
      <div className="lp-scrap lp-scrap--note">
        TDS 26Q —<br />FRIDAY!!
        <em className="lp-scrap-missed">MISSED</em>
      </div>
      <div className="lp-scrap lp-scrap--xls">
        <b>compliance_FINAL_v7.xlsx</b>
        <span className="lp-scrap-cells" />
      </div>
      <div className="lp-scrap lp-scrap--call">
        <Icon name="phone" size={12} /> 3 missed calls · Kalyan Textiles
      </div>
      <div className="lp-scrap lp-scrap--fee">Late fee ₹5,000 + interest</div>

      {/* the register */}
      <div className="lp-scene-register">
        <div className="lp-scene-reg-head">July — every client covered</div>
        {[
          ["GSTR-3B × 27 clients — auto-created", "g"],
          ["TDS 26Q — assigned to Arjun", "g"],
          ["Advance tax reminders — sent", "g"],
          ["ITR — Sharma & Co — in review", "w"],
        ].map(([t, tone], i) => (
          <div className={`lp-scene-row t-${tone}`} key={t} style={{ transitionDelay: `${300 + i * 130}ms` }}>
            <span className="lp-scene-tick"><Icon name={tone === "w" ? "clock" : "check"} size={11} /></span>
            {t}
          </div>
        ))}
        <InkStamp />
      </div>
    </div>
  );
}

/* ── CSS product mocks (no screenshots needed) ── */

/* Circular red ink stamp, planted over the hero screenshot's corner. */
function InkStamp() {
  return (
    <svg className="lp-stamp" viewBox="0 0 140 140" aria-hidden>
      <defs>
        <path id="lp-stamp-arc" d="M70 70 m -51 0 a 51 51 0 1 1 102 0 a 51 51 0 1 1 -102 0" />
      </defs>
      <circle cx="70" cy="70" r="62" fill="none" stroke="currentColor" strokeWidth="3" />
      <circle cx="70" cy="70" r="39" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <text fill="currentColor" fontSize="12" fontWeight="700" letterSpacing="2.8">
        <textPath href="#lp-stamp-arc">SMART CA · VIJAYAWADA · PRACTICE IN ORDER ·</textPath>
      </text>
      <text x="70" y="67" textAnchor="middle" fill="currentColor" fontSize="15" fontWeight="800" letterSpacing="1.5">FILED</text>
      <text x="70" y="83" textAnchor="middle" fill="currentColor" fontSize="10.5" fontWeight="700" letterSpacing="2.4">ON TIME</text>
    </svg>
  );
}

/* Colour-coded monthly muster — the attendance register, miniature. */
function MusterMock() {
  const days = Array.from({ length: 12 }, (_, i) => i + 1);
  const rows = [
    ["Priya", ["P", "P", "½", "P", "P", "W", "H", "P", "L", "P", "P", "P"]],
    ["Arjun", ["P", "L", "P", "P", "P", "W", "H", "½", "P", "P", "A", "P"]],
    ["Neha", ["P", "P", "P", "½", "P", "W", "H", "P", "P", "L", "P", "P"]],
    ["Vikram", ["½", "P", "P", "P", "P", "W", "H", "P", "P", "P", "P", "P"]],
  ];
  const cls = { P: "p", "½": "hd", L: "l", A: "a", W: "wo", H: "ho" };
  const shown = { P: "P", "½": "½", L: "L", A: "A", W: "·", H: "H" };
  const legend = [["p", "Present"], ["hd", "Half-day"], ["l", "Leave"], ["a", "Absent"], ["ho", "Holiday"]];
  return (
    <div className="lp-muster" aria-hidden>
      <div className="lp-muster-head">
        <span className="lp-muster-title">July muster</span>
        <span className="lp-muster-legend">
          {legend.map(([k, label]) => (
            <span key={k}><i className={`lm-${k}`} />{label}</span>
          ))}
        </span>
      </div>
      <div className="lp-muster-grid">
        <span className="lp-muster-corner" />
        {days.map((d) => <span key={`d${d}`} className="lp-muster-day">{d}</span>)}
        {rows.map(([name, cells], r) => (
          <FragmentRow key={name} name={name} cells={cells} cls={cls} shown={shown} row={r} />
        ))}
      </div>
      <div className="lp-muster-foot">
        <span className="lp-muster-live" />
        <b>Priya</b>&nbsp;checked in · 9:42 am — inside HQ geofence
      </div>
    </div>
  );
}

function FragmentRow({ name, cells, cls, shown, row = 0 }) {
  return (
    <>
      <span className="lp-muster-name">{name}</span>
      {cells.map((c, i) => (
        <span
          key={i}
          className={`lp-muster-cell lm-${cls[c]}`}
          style={{ animationDelay: `${200 + (row * cells.length + i) * 16}ms` }}
        >
          {shown[c]}
        </span>
      ))}
    </>
  );
}

/* Workload-by-person bars. */
function OversightMock() {
  const rows = [
    ["Priya", 12, 13],
    ["Arjun", 9, 12],
    ["Neha", 8, 11],
    ["Vikram", 5, 10],
  ];
  return (
    <div className="lp-omock" aria-hidden data-reveal>
      <div className="lp-omock-head">
        <span className="lp-omock-title">This week, by person</span>
        <span className="lp-omock-sub">tasks done</span>
      </div>
      {rows.map(([n, done, total], i) => (
        <div className="lp-omock-row" key={n}>
          <span className="lp-omock-name">{n}</span>
          <span className="lp-omock-bar">
            <span style={{ width: `${Math.round((done / total) * 100)}%`, transitionDelay: `${250 + i * 130}ms` }} />
          </span>
          <span className="lp-omock-count">{done}/{total}</span>
        </div>
      ))}
    </div>
  );
}

/* Attendance story scene: check-in card → engine chips → the muster lands. */
function AttendanceScene() {
  return (
    <div className="lp-attscene" aria-hidden>
      <div className="lp-att-card">
        <div className="lp-att-row"><span className="lp-att-live" /> Priya S · Front desk</div>
        <div className="lp-att-time">9:42<small> am</small></div>
        <div className="lp-att-shift">Shift 9:30 – 6:00 · HQ</div>
        <div className="lp-att-btn"><Icon name="check" size={13} /> Checked in</div>
      </div>
      <div className="lp-att-chip c1">Late · 12 min</div>
      <div className="lp-att-chip c2">Worked · 7h 51m</div>
      <div className="lp-att-chip c3">Shortfall · 9 min</div>
      <div className="lp-att-chip c4">Half-day CL · approved</div>
      <div className="lp-att-muster"><MusterMock /></div>
      <div className="lp-att-csv"><Icon name="download" size={12} /> muster_july.csv · 10 reports</div>
    </div>
  );
}

/* Geofence story scene: fence draws → Priya walks in, unlocks → Rahul locks out. */
function GeoScene() {
  return (
    <div className="lp-geomock lp-geoscene" data-reveal="bare" aria-hidden>
      <div className="lp-geomock-grid" />
      <svg className="lp-geomock-svg" viewBox="0 0 320 220" preserveAspectRatio="none">
        <polygon
          className="lp-fence lp-geo-fence"
          points="70,45 235,40 250,150 108,168"
          fill="rgba(111,207,159,0.14)"
          stroke="#6fcf9f"
          strokeWidth="2"
          strokeDasharray="6 5"
          strokeLinejoin="round"
        />
      </svg>
      <div className="lp-geo-pin">
        <svg viewBox="0 0 24 24" width="34" height="34">
          <path d="M12 22s7-6.7 7-12.5A7 7 0 0 0 5 9.5C5 15.3 12 22 12 22z" fill="#bf4136" stroke="#f7f3e9" strokeWidth="1.3" strokeLinejoin="round" />
          <circle cx="12" cy="9.5" r="3" fill="#f7e8c8" />
        </svg>
      </div>
      <div className="lp-geo-person lp-geo-priya"><i className="lp-geo-dot" /><span>Priya</span></div>
      <div className="lp-geo-person lp-geo-rahul"><i className="lp-geo-dot" /><span>Rahul</span></div>
      <div className="lp-geo-tag"><Icon name="check" size={11} /> Punch tagged · HQ geofence</div>
      <div className="lp-geo-lock"><Icon name="lock" size={12} /> Locked — outside HQ area</div>
      <span className="lp-geomock-cap"><Icon name="map-pin" size={12} /> HQ geofence · 5 assigned</span>
    </div>
  );
}

/* ── Lead capture form ── */
const EMPTY = { name: "", firm_name: "", email: "", phone: "", message: "", website: "" };

function LeadForm() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    if (!form.name.trim()) { setErrors({ name: ["Please enter your name."] }); return; }
    if (!form.email.trim() && !form.phone.trim()) {
      setErrors({ email: ["Provide an email or a phone number."] });
      return;
    }
    setSending(true);
    try {
      await submitLead(form);
      setDone(true);
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      else setErrors({ form: [err?.message || "Something went wrong. Please try again."] });
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="lp-form lp-form--done">
        <div className="lp-done-icon"><Icon name="check-circle" size={28} /></div>
        <h3>Thanks — we'll be in touch.</h3>
        <p>We've received your enquiry and will reach out shortly.</p>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={submit} noValidate data-reveal>
      {/* Honeypot: hidden from humans; bots fill it and get silently dropped. */}
      <input type="text" name="website" className="lp-hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
        value={form.website} onChange={(e) => set({ website: e.target.value })} />

      <div className="field">
        <label className="field-label">Your name *</label>
        <input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="CA Name" />
        {errors.name && <div className="field-error">{errors.name[0]}</div>}
      </div>
      <div className="field">
        <label className="field-label">Firm name</label>
        <input className="input" value={form.firm_name} onChange={(e) => set({ firm_name: e.target.value })} placeholder="Your practice" />
      </div>
      <div className="lp-form-row">
        <div className="field">
          <label className="field-label">Email</label>
          <input className="input" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="you@firm.com" />
          {errors.email && <div className="field-error">{errors.email[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Phone</label>
          <input className="input" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+91…" />
        </div>
      </div>
      <div className="field">
        <label className="field-label">Message</label>
        <textarea className="textarea" rows={4} value={form.message} onChange={(e) => set({ message: e.target.value })} placeholder="How big is your team? What are you using today?" />
      </div>
      {errors.form && <div className="field-error">{errors.form[0]}</div>}
      <button type="submit" className="lp-btn lp-btn--primary lp-btn--lg" disabled={sending}>
        {sending ? "Sending…" : <>Request a demo <Icon name="arrow-right" size={15} /></>}
      </button>
    </form>
  );
}
